/**
 * DETALLE de un plan de lectura: lista de días con sus capítulos, enlace directo
 * al lector y marcado LOCAL-FIRST de día completado (sincroniza vía
 * `bible_reading_progress`; racha/XP se derivan de estas filas en Logros).
 */
import { msg } from "@lingui/core/macro";
import { createRoute, useParams } from "@tanstack/solid-router";
import { createMemo, For, Show } from "solid-js";
import { ArrowLeft, BookOpen, Check, ListChecks } from "lucide-solid";
import { Link, useNavigate } from "@/lib/u-router";
import { appRoute } from "./route";
import { useI18n } from "@/providers/i18n-provider";
import { PageContainer } from "@/components/ui/page-container";
import { NotFoundState } from "@/components/ui/not-found-state";
import { Card, CardContent } from "@/components/ui/card";
import { getBookNameDescriptor, idToAbbr } from "@/utils/bible-books-data";
import { getPlanTemplate } from "@/features/reading-plans/data/plan-templates";
import { computePlanProgress, type PlanReading } from "@/features/reading-plans/logic";
import { useReadingProgress } from "@/features/reading-plans/hooks/use-reading-progress";

export const planDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/plans/$planId",
  component: PlanDetailPage,
});

const DEFAULT_VERSION = "kjv";
const PERCENT_MAX = 100;

function PlanDetailPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const params = useParams({ from: "/u/$u/plans/$planId" });
  const { activeRows, completeDay, uncompleteDay, ready } = useReadingProgress();

  const plan = createMemo(() => getPlanTemplate(params().planId));
  const view = createMemo(() => {
    const template = plan();
    if (!template) {
      return null;
    }
    return computePlanProgress(activeRows(), template.id, template.days.length);
  });

  const readingLabel = (reading: PlanReading) =>
    `${t(getBookNameDescriptor(reading.book))} ${reading.chapter}`;

  const toggleDay = (day: number) => {
    const template = plan();
    if (!template) {
      return;
    }
    if (view()?.completedDays.has(day)) {
      void uncompleteDay(template.id, day);
    } else {
      void completeDay(template.id, day);
    }
  };

  return (
    <Show
      when={plan()}
      fallback={
        <NotFoundState
          title={t(msg`Plan not found`)}
          description={t(msg`This reading plan does not exist.`)}
          primaryAction={{
            label: t(msg`Back to reading plans`),
            onClick: () => {
              void navigate({ to: "/plans" });
            },
          }}
        />
      }
    >
      {(template) => (
        <PageContainer maxWidth="3xl">
          <div class="space-y-5 pb-24" data-testid="plan-detail-page">
            <Link
              to="/plans"
              class="inline-flex items-center gap-1.5 text-xs font-bold text-muted-foreground hover:text-primary transition-colors"
              data-testid="plan-back-link"
            >
              <ArrowLeft class="w-3.5 h-3.5" />
              {t(msg`Reading plans`)}
            </Link>

            <div class="flex items-start gap-3">
              <div class="p-2.5 rounded-xl bg-primary/10 border border-primary/20 text-primary">
                <ListChecks class="h-5 w-5" />
              </div>
              <div class="min-w-0">
                <h1 class="text-xl font-bold tracking-tight font-serif">{t(template().title)}</h1>
                <p class="text-xs text-muted-foreground mt-0.5">{t(template().description)}</p>
              </div>
            </div>

            {/* Avance */}
            <Card>
              <CardContent class="pt-5 space-y-2">
                <div class="flex items-center justify-between text-xs font-bold text-muted-foreground">
                  <span data-testid="plan-progress-count">
                    {view()?.completedCount ?? 0} / {template().days.length} {t(msg`days`)}
                  </span>
                  <span data-testid="plan-progress-percent">{view()?.percent ?? 0}%</span>
                </div>
                <div class="h-2.5 w-full rounded-full bg-secondary/25 overflow-hidden">
                  <div
                    class="h-full rounded-full bg-primary motion-safe:transition-[width] motion-safe:duration-500"
                    style={{ width: `${Math.min(view()?.percent ?? 0, PERCENT_MAX)}%` }}
                  />
                </div>
                <Show when={view()?.isComplete}>
                  <p class="text-xs font-bold text-primary" data-testid="plan-complete-banner">
                    {t(msg`Plan completed — well done!`)}
                  </p>
                </Show>
              </CardContent>
            </Card>

            {/* Días */}
            <div class="space-y-2.5" data-testid="plan-days-list">
              <For each={template().days}>
                {(day, index) => {
                  const dayNumber = () => index() + 1;
                  const done = () => view()?.completedDays.has(dayNumber()) ?? false;
                  const first = () => day.readings[0];
                  return (
                    <div
                      class="flex items-center gap-3 rounded-xl border border-border/35 bg-card/30 px-4 py-3"
                      classList={{ "border-primary/40": done() }}
                      data-testid={`plan-day-${dayNumber()}`}
                      data-completed={done() ? "true" : "false"}
                    >
                      <button
                        type="button"
                        disabled={!ready()}
                        onClick={() => {
                          toggleDay(dayNumber());
                        }}
                        class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        classList={{
                          "border-primary bg-primary text-primary-foreground": done(),
                          "border-border/60 text-transparent hover:border-primary/60": !done(),
                        }}
                        aria-pressed={done()}
                        aria-label={t(msg`Mark day as read`)}
                        data-testid={`plan-day-toggle-${dayNumber()}`}
                      >
                        <Check class="w-4 h-4" />
                      </button>
                      <div class="min-w-0 flex-1">
                        <div class="text-3xs font-bold uppercase tracking-wider text-muted-foreground">
                          {t(msg`Day`)} {dayNumber()}
                        </div>
                        <div class="text-sm font-semibold text-foreground/90 truncate">
                          {day.readings.map(readingLabel).join(" · ")}
                        </div>
                      </div>
                      <Show when={first()}>
                        {(reading) => (
                          <Link
                            to="/bible/$version/$bookAbbr/$chapter"
                            params={{
                              version: DEFAULT_VERSION,
                              bookAbbr: idToAbbr(reading().book),
                              chapter: String(reading().chapter),
                            }}
                            class="shrink-0 p-2 text-muted-foreground hover:text-primary transition-colors"
                            title={t(msg`Read passage`)}
                            data-testid={`plan-day-read-${dayNumber()}`}
                          >
                            <BookOpen class="w-4 h-4" />
                          </Link>
                        )}
                      </Show>
                    </div>
                  );
                }}
              </For>
            </div>
          </div>
        </PageContainer>
      )}
    </Show>
  );
}
